<template>
  <BaseModal
    v-model="showModal"
    title="Configuration de l'entreprise"
    size="md"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white shadow-lg">
          <BaseIcon name="settings" :size="24" />
        </div>
        <div>
          <h2 class="text-xl font-semibold text-gray-900">Configuration de l'entreprise</h2>
          <p class="text-sm text-gray-500">Paramètres avancés de votre entreprise</p>
        </div>
      </div>
    </template>

    <!-- Loading state -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-16 gap-4">
      <BaseSpinner size="lg" />
      <p class="text-sm text-gray-500">Chargement des paramètres...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="loadError" class="flex flex-col items-center justify-center py-16 gap-4">
      <div class="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center text-red-500">
        <BaseIcon name="alert-triangle" :size="28" />
      </div>
      <p class="text-sm text-gray-500">Erreur lors du chargement des paramètres</p>
      <BaseButton variant="primary" size="sm" @click="loadSettings">
        <template #icon-left>
          <BaseIcon name="refresh" :size="16" />
        </template>
        Réessayer
      </BaseButton>
    </div>

    <!-- Settings form -->
    <form v-else @submit.prevent="saveSettings" class="space-y-6">
      <!-- Section: Ventes -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="shopping-cart" :size="18" />
          <span>Paramètres de vente</span>
        </div>

        <div class="space-y-3">
          <!-- Allow zero quantity sales -->
          <div class="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div class="flex-1">
              <label class="block text-sm font-semibold text-gray-800 mb-1">
                Autoriser les ventes avec quantité zéro
              </label>
              <p class="text-xs text-gray-500 leading-relaxed">
                Permet de créer des ventes pour des articles avec un stock à zéro
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4">
              <input type="checkbox" v-model="form.allow_zero_quantity_sales" class="sr-only peer" />
              <div class="w-11 h-6 bg-gray-300 peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <!-- Update product price on sale -->
          <div class="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div class="flex-1">
              <label class="block text-sm font-semibold text-gray-800 mb-1">
                Mettre à jour le prix du produit lors d'une vente
              </label>
              <p class="text-xs text-gray-500 leading-relaxed">
                Lorsqu'une vente est effectuée avec un prix modifié, met à jour automatiquement le prix du produit
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer ml-4">
              <input type="checkbox" v-model="form.update_product_price_on_sale" class="sr-only peer" />
              <div class="w-11 h-6 bg-gray-300 peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>

      <!-- Section: Caisses -->
      <div>
        <div class="flex items-center gap-2 text-primary font-semibold mb-4 pb-2 border-b-2 border-primary/20">
          <BaseIcon name="monitor" :size="18" />
          <span>Paramètres des caisses</span>
        </div>

        <div class="flex flex-col gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div>
            <label class="block text-sm font-semibold text-gray-800 mb-1">
              Nombre maximum de caisses
            </label>
            <p class="text-xs text-gray-500 leading-relaxed">
              Limite le nombre de caisses enregistreuses (laisser vide pour illimité)
            </p>
          </div>
          <div class="flex items-center gap-4">
            <input
              type="number"
              v-model.number="form.max_cash_registers"
              :disabled="isUnlimited"
              min="1"
              max="100"
              placeholder="Ex: 5"
              class="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            />
            <label class="flex items-center gap-2 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                v-model="isUnlimited"
                @change="onUnlimitedChange"
                class="w-4 h-4 accent-primary cursor-pointer"
              />
              <span class="text-sm font-medium text-gray-700">Illimité</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Reset Section -->
      <div class="pt-5 border-t border-gray-200 text-center">
        <button
          type="button"
          class="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-500 bg-transparent border border-dashed border-gray-300 rounded-lg hover:text-yellow-600 hover:border-yellow-500 hover:bg-yellow-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isResetting"
          @click="confirmReset"
        >
          <BaseIcon name="refresh" :size="16" />
          Réinitialiser aux valeurs par défaut
        </button>
      </div>
    </form>

    <template #footer v-if="!loading && !loadError">
      <div class="flex justify-end gap-3 w-full">
        <BaseButton variant="ghost" :disabled="isSaving" @click="closeModal">
          Annuler
        </BaseButton>
        <BaseButton
          variant="primary"
          :loading="isSaving"
          @click="saveSettings"
        >
          <template #icon-left>
            <BaseIcon name="check" :size="18" />
          </template>
          Enregistrer
        </BaseButton>
      </div>
    </template>
  </BaseModal>

  <!-- Reset Confirmation Dialog -->
  <BaseConfirmDialog
    v-model="showResetConfirm"
    title="Réinitialiser les paramètres ?"
    message="Cette action rétablira tous les paramètres aux valeurs par défaut."
    variant="warning"
    confirm-text="Réinitialiser"
    :loading="isResetting"
    @confirm="executeReset"
    @cancel="cancelReset"
  />
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue'
import { companySettingService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseIcon, BaseSpinner, BaseConfirmDialog } from '@/components/base'

export default defineComponent({
  name: 'CompanySettingsModal',
  components: {
    BaseModal,
    BaseButton,
    BaseIcon,
    BaseSpinner,
    BaseConfirmDialog
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'settings-updated'],
  setup(props, { emit }) {
    const toastStore = useToastStore()

    const loading = ref(false)
    const loadError = ref(false)
    const isSaving = ref(false)
    const isResetting = ref(false)
    const showResetConfirm = ref(false)
    const isUnlimited = ref(true)

    const form = ref({
      allow_zero_quantity_sales: false,
      update_product_price_on_sale: false,
      max_cash_registers: null
    })

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close')
        }
      }
    })

    const loadSettings = async () => {
      try {
        loading.value = true
        loadError.value = false
        const response = await companySettingService.getCompanySettings()
        const settings = response.data.data || response.data

        form.value = {
          allow_zero_quantity_sales: settings.allow_zero_quantity_sales || false,
          update_product_price_on_sale: settings.update_product_price_on_sale || false,
          max_cash_registers: settings.max_cash_registers
        }

        localStorage.setItem('company_settings', JSON.stringify({
          allow_zero_quantity_sales: form.value.allow_zero_quantity_sales,
          update_product_price_on_sale: form.value.update_product_price_on_sale,
          max_cash_registers: form.value.max_cash_registers
        }))

        isUnlimited.value = settings.max_cash_registers === null
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error)
        loadError.value = true
      } finally {
        loading.value = false
      }
    }

    const onUnlimitedChange = () => {
      if (isUnlimited.value) {
        form.value.max_cash_registers = null
      } else {
        form.value.max_cash_registers = 1
      }
    }

    const saveSettings = async () => {
      try {
        isSaving.value = true

        const settingsData = {
          allow_zero_quantity_sales: form.value.allow_zero_quantity_sales,
          update_product_price_on_sale: form.value.update_product_price_on_sale,
          max_cash_registers: isUnlimited.value ? null : form.value.max_cash_registers
        }

        await companySettingService.updateCompanySettings(settingsData)
        localStorage.setItem('company_settings', JSON.stringify(settingsData))

        toastStore.addToast({
          type: 'success',
          message: 'Paramètres enregistrés avec succès',
          duration: 4000
        })

        emit('settings-updated')
        closeModal()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des paramètres:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de la sauvegarde des paramètres',
          duration: 4000
        })
      } finally {
        isSaving.value = false
      }
    }

    const confirmReset = () => {
      showResetConfirm.value = true
    }

    const cancelReset = () => {
      showResetConfirm.value = false
    }

    const executeReset = async () => {
      try {
        isResetting.value = true
        await companySettingService.resetCompanySettings()

        toastStore.addToast({
          type: 'success',
          message: 'Paramètres réinitialisés aux valeurs par défaut',
          duration: 4000
        })

        showResetConfirm.value = false
        await loadSettings()
        emit('settings-updated')
      } catch (error) {
        console.error('Erreur lors de la réinitialisation:', error)
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de la réinitialisation',
          duration: 4000
        })
      } finally {
        isResetting.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        loadSettings()
      } else {
        showResetConfirm.value = false
      }
    })

    return {
      showModal,
      loading,
      loadError,
      isSaving,
      isResetting,
      showResetConfirm,
      isUnlimited,
      form,
      loadSettings,
      onUnlimitedChange,
      saveSettings,
      confirmReset,
      cancelReset,
      executeReset,
      closeModal
    }
  }
})
</script>
